import { AuthenticatedContextProvider } from '@luminovo/auth';
import { id, logToExternalErrorHandlers } from '@luminovo/commons';
import { analytics } from '@luminovo/data-stack';
import { type RouteChainOf, useNavigate } from '@luminovo/design-system';
import { LinkAccessProvider } from '@luminovo/design-system/router-kit';
import { SlotAnalyticsProvider, SlotErrorReportingProvider } from '@luminovo/slots';
import React from 'react';

import { ModalContextProvider } from '@/shared/data/contexts/ModalContext';
import { useTenantDetailsContext } from '@/shared/data/contexts/TenantDetailsContext';
import { canOpenRoute } from '@/shared/permissions/PagePermissionContext';
import { useSession, useWatchSession } from '@/shared/permissions/session';
import { ErrorBoundaryClearedOnNavigation } from '@/shared/ui/errors/ErrorBoundaryClearedOnNavigation';
import { pageToReturnToAfterSignIn } from '@/shared/utils/loginRedirect';
import { createSlotAnalyticsTrack } from '@/shared/utils/services/slotAnalytics';

import AuthenticatedApp from './AuthenticatedApp';
import { IntercomWidget } from './IntercomWidget';
import { reloadPage } from './reloadPage';
import { reportTokenRenewal } from './tokenRenewalTelemetry';

/**
 * Lets nav links ask whether the session may open the page they point to. Here, not in `_authenticated`'s component:
 * the app-route tests mock this layout, and they never fill the session queries.
 */
function SessionLinkAccess({ children }: { children: React.ReactNode }): JSX.Element {
    const session = useSession();
    const canOpen = React.useCallback((chain: ReturnType<RouteChainOf>) => canOpenRoute(session, chain), [session]);
    return <LinkAccessProvider canOpen={canOpen}>{children}</LinkAccessProvider>;
}

/** The signed-in shell; a user signed out from the start never gets here, as `beforeLoad` redirects first. */
export function AuthenticatedLayout({ children }: { children: React.ReactNode }): JSX.Element {
    // Around `NotAuthorizedPage` too, so the session `beforeLoad` loaded stays cached for the next navigation.
    useWatchSession();
    const { tenantDetails } = useTenantDetailsContext();
    const auth0OrgId = tenantDetails.external_auth_org_id ?? undefined;
    const trackSlotEvent = React.useMemo(() => createSlotAnalyticsTrack(tenantDetails.tenant), [tenantDetails.tenant]);
    const navigate = useNavigate();

    return (
        <AuthenticatedContextProvider
            onNotAuthenticated={({ redirectTo, again }) => {
                const page = pageToReturnToAfterSignIn(redirectTo);
                // If the address bar showed a sign-in page, the redirect already on its way counted the lost session.
                if (!again && page === redirectTo) {
                    analytics.track(id('auth/email_form_redirected'), { source: 'provider' });
                }
                navigate({
                    to: '/auth/enter-email',
                    search: { redirectTo: page },
                    // A pushed redirect left the protected page behind login, so Back redirected again.
                    replace: true,
                    // Past the guard: without a session every request fails, so "Stay" would only trap the user.
                    ignoreBlocker: true,
                });
            }}
            // Another account signed in in this browser: the pages on screen are the previous user's.
            onUserChanged={reloadPage}
            onTokenRenewal={reportTokenRenewal}
            auth0OrgId={auth0OrgId}
        >
            <ErrorBoundaryClearedOnNavigation>
                {/* Above everything that reads slot assignments, such as the pages and the command palette. */}
                <SlotAnalyticsProvider track={trackSlotEvent}>
                    <SlotErrorReportingProvider reportError={logToExternalErrorHandlers}>
                        <SessionLinkAccess>
                            <ModalContextProvider>
                                <AuthenticatedApp>{children}</AuthenticatedApp>
                                <ErrorBoundaryClearedOnNavigation>
                                    <IntercomWidget />
                                </ErrorBoundaryClearedOnNavigation>
                            </ModalContextProvider>
                        </SessionLinkAccess>
                    </SlotErrorReportingProvider>
                </SlotAnalyticsProvider>
            </ErrorBoundaryClearedOnNavigation>
        </AuthenticatedContextProvider>
    );
}
